import { useMemo } from "react";
import { useSelector } from "react-redux";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { RootState } from "../../../../../../../redux/store";
import ChartCard, { ChartEmptyState } from "./ChartCard";
import VerifiedIcon from "../../../../../../../assets/svg/VerifiedIcon";
import { SlaComplianceData } from "../utils/constants";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";

const RADIAN = Math.PI / 180;

interface PieSegment {
  id: string;
  value: number;
  color: string;
  label: string | null;
}

interface LegendItem {
  label: string;
  percent: string;
  count: number;
  color: string;
}

type LabelProps = {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  index: number;
};

interface SLAComplianceChartProps {
  slaData: SlaComplianceData;
}

const SLAComplianceChart = ({ slaData }: SLAComplianceChartProps) => {
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const isDarkMode = useSelector(
    (state: RootState) => state?.themeProvider?.isDarkMode
  );
  const dividerColor = isDarkMode ? "#393f4a" : "#e9eaeb";
  const countColor = isDarkMode ? "#c0c2c5" : "#636770";
  const headingColor = isDarkMode ? "#fbfbfb" : "#252b37";

  const { pieSegments, legend } = useMemo(() => {
    const withoutPct = slaData?.withoutSla?.percentage;
    const withinPct = slaData?.withinSla?.percentage;

    const segments: PieSegment[] = [
      { id: "salmon_a", value: withoutPct, color: "#ee8c8b", label: `${Math.round(withoutPct)}%` },
      { id: "green_a", value: withinPct, color: "#10b981", label: `${Math.round(withinPct)}%` },
    ];

    const legendItems: LegendItem[] = [
      {
        label: "Within SLA period",
        percent: `${Math.round(withinPct)}%`,
        count: slaData?.withinSla?.count,
        color: "#10b981",
      },
      {
        label: "Without SLA period",
        percent: `${Math.round(withoutPct)}%`,
        count: slaData?.withoutSla?.count,
        color: "#ee8c8b",
      },
    ];

    return { pieSegments: segments, legend: legendItems };
  }, [slaData]);

  const SliceLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    index,
  }: LabelProps) => {
    const seg = pieSegments?.[index];
    if (!seg?.label || seg?.value === 0 || seg?.value <= 4) return null;

    const angle = midAngle;
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-angle * RADIAN);
    const y = cy + r * Math.sin(-angle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        {seg?.label}
      </text>
    );
  };

  return (
    <ChartCard
      title="SLA Compliance Report"
      description={`${helpdeskLabels.ticketPlural} closed within and outside SLA timeframes`}
      headerIcon={
        <VerifiedIcon className="w-4 h-4 text-analytics-green dark:text-analytics-green-dk" />
      }
      className="!gap-1 max-h-[286px]"
    >
      {slaData?.total === 0 ? (
        <ChartEmptyState />
      ) : (
      <div className="flex items-center justify-between w-full -mt-1!">
        <div className="flex flex-col gap-[18px] shrink-0">
          {legend?.map((item, idx) => (
            <div key={item?.label}>
              <div className="flex gap-2.5 items-center">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: item?.color }}
                />
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xl font-medium leading-none"
                      style={{ color: headingColor }}
                    >
                      {item?.percent}
                    </span>
                    <div
                      className="w-px h-3.5 shrink-0"
                      style={{ backgroundColor: dividerColor }}
                    />
                    <span
                      className="text-base font-medium leading-none"
                      style={{ color: countColor }}
                    >
                      {item?.count}
                    </span>
                  </div>
                  <span
                    className="text-md leading-[20px]"
                    style={{ color: headingColor }}
                  >
                    {item.label}
                  </span>
                </div>
              </div>

              {idx === 0 && (
                <div
                  className="h-px w-full mt-[18px]"
                  style={{ backgroundColor: dividerColor }}
                />
              )}
            </div>
          ))}
        </div>

        <div className="relative shrink-0 w-[222px] h-[222px]">
          <div className="relative z-10 w-full h-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Pie
                data={pieSegments as unknown as Record<string, unknown>[]}
                dataKey="value"
                startAngle={150}
                endAngle={-240}
                innerRadius="58%"
                outerRadius="92%"
                stroke="none"
                paddingAngle={0}
                label={(props: LabelProps) => <SliceLabel {...props} />}
                labelLine={false}
                isAnimationActive={false}
              >
                {pieSegments?.map((seg) => (
                  <Cell key={seg?.id} fill={seg?.color} />
                ))}
              </Pie>
              <Tooltip
                offset={0}
                allowEscapeViewBox={{ x: true, y: true }}
                content={({ active, payload, coordinate }) => {
                  if (!active || !payload?.length) return null;
                  const seg = payload?.[0]?.payload as PieSegment;
                  const hoveredColor = seg?.color;
                  const cursorX = coordinate?.x ?? 0;
                  const cursorY = coordinate?.y ?? 0;
                  return (
                    <div
                      className="w-[230px] flex flex-col gap-2 bg-tooltip-bg dark:bg-tooltip-bg-dk py-2 text-white text-xs rounded-[6px] shadow-md pointer-events-none"
                      style={{
                        position: "absolute",
                        left: cursorX,
                        top: cursorY,
                        transform: "translate(-105%, -105%)",
                      }}
                    >
                      <h4 className="text-xs pb-1 px-3 text-pure-white dark:text-pure-white-dk">
                        Tickets Closed
                      </h4>
                      {legend?.map((item) => {
                        const isHovered = item?.color === hoveredColor;
                        return (
                          <div
                            key={item.label}
                            className={`grid items-center gap-x-2 rounded-[4px] px-2 py-0.5 grid-cols-[10px_1fr_auto_auto] ${
                              isHovered
                                ? "bg-analytics-tooltip-hover dark:bg-analytics-tooltip-hover-dk"
                                : "bg-transparent"
                            }`}
                          >
                            <span
                              className="w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: item?.color }}
                            />
                            <p className="text-xs text-pure-white dark:text-pure-white-dk truncate">
                  {item?.label}
                </p>
                            <p className="text-xs text-right tabular-nums text-pure-white dark:text-pure-white-dk font-bold">
                              {item?.count}
                            </p>
                            <p className="text-xs text-right tabular-nums font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                              {item?.percent}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          </div>

          <div className="absolute inset-0 z-0 flex flex-col items-center justify-center pointer-events-none">
            <p className="text-xl font-medium leading-none text-heading-text dark:text-heading-text-dk">
              {slaData?.total}
            </p>
            <p className="text-sm leading-none mt-[7px] text-center whitespace-nowrap text-dark-gray dark:text-dark-gray-dk">
              Total {helpdeskLabels.ticketPlural}
            </p>
          </div>
        </div>
      </div>
      )}
    </ChartCard>
  );
};

export default SLAComplianceChart;
